import { useEffect, useState } from "react";
import { Plus, Trash2, Search, Download } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/portal/widgets";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";

const CATS = ["Fruits & Vegetables", "Dairy & Bakery", "Staples & Pulses", "Snacks & Beverages", "Personal Care", "Household"];

function csv(rows) {
  const head = ["Name", "Category", "Price", "Unit", "Deal"];
  const body = rows.map((p) => [p.name, p.category, p.price, p.unit, p.on_deal ? "Yes" : "No"]);
  const content = [head, ...body].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
  const blob = new Blob([content], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "products.csv"; a.click();
}

export default function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", category: "", price: "", unit: "", description: "", on_deal: false });

  const load = () => api.get("/products?limit=500").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.name || !f.category || !f.price || !f.unit) { toast.error("Fill all required fields"); return; }
    try {
      await api.post("/admin/products", { ...f, price: parseFloat(f.price) });
      toast.success("Product added"); setOpen(false); setF({ name: "", category: "", price: "", unit: "", description: "", on_deal: false }); load();
    } catch (e) { toast.error("Failed to add"); }
  };

  const del = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="animate-fade-up">
      <PageHeader title="Product Master" subtitle={`${products.length} products across ${CATS.length} categories.`}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => csv(products)} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="add-product-btn" className="bg-yellow-400 hover:bg-yellow-500 text-slate-900"><Plus className="w-4 h-4 mr-1" /> Add Product</Button></DialogTrigger>
              <DialogContent data-testid="add-product-modal">
                <DialogHeader><DialogTitle className="font-heading">New Product</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name *</Label><Input data-testid="pf-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
                  <div><Label>Category *</Label>
                    <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                      <SelectTrigger data-testid="pf-category"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Price (₹) *</Label><Input data-testid="pf-price" type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></div>
                    <div><Label>Unit *</Label><Input data-testid="pf-unit" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="per kg" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.on_deal} onChange={(e) => setF({ ...f, on_deal: e.target.checked })} /> Mark as deal</label>
                  <Button onClick={add} data-testid="pf-submit" className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900">Add Product</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        } />

      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="pl-9" />
      </div>

      <Card className="border-slate-200/80 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
              <th className="py-3 px-4">Product</th><th className="py-3 px-4">Category</th><th className="py-3 px-4">Price</th><th className="py-3 px-4">Unit</th><th className="py-3 px-4"></th>
            </tr></thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-2.5 px-4 flex items-center gap-3"><img src={p.image} alt="" className="w-9 h-9 rounded object-cover" /> <span className="font-medium">{p.name}</span> {p.on_deal && <Badge className="bg-orange-100 text-orange-700">Deal</Badge>}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{p.category}</td>
                  <td className="py-2.5 px-4 font-semibold">{inr(p.price)}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{p.unit}</td>
                  <td className="py-2.5 px-4 text-right"><button onClick={() => del(p.id)} data-testid={`delete-product-${p.id}`} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
